import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { getInstitutionalFilter, insertInstitutionalOrder } from '../utilities/api';
// PrimeReact Components
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';

const InstitutionSales = () => {
    const navigate = useNavigate();
    const toast = useRef(null);
    // States
    const [allData, setAllData] = useState({ company: [], category: [], products: [] });
    const [filteredCategories, setFilteredCategories] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);

    // Selectable States
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);

    const [cartItems, setCartItems] = useState([]);
    const [remarks, setRemarks] = useState('');
    const [orderTakenBy, setOrderTakenBy] = useState('');
    const [deliveryDate, setDeliveryDate] = useState(new Date());
    const [deliveryTime, setDeliveryTime] = useState(null);
    const [sessionInfo, setSessionInfo] = useState({ ubranch: null, userid: null, branchid: null });
    const [loading, setLoading] = useState(false);

    // Date & Time States
    const [orderDate, setOrderDate] = useState(new Date());
    const [orderTime, setOrderTime] = useState(new Date());

    // Initial Data Fetch
    useEffect(() => {
        const fetchData = async () => {
            const bid = localStorage.getItem('branchid');
            const branchName = localStorage.getItem('branch');
            const uid = localStorage.getItem('userid');

            setSessionInfo({ ubranch: branchName, userid: uid, branchid: bid });

            try {
                const result = await getInstitutionalFilter({ branch_id: bid });
                setAllData(result || { company: [], category: [], products: [] });
            } catch (err) {
                console.error('Fetch Error:', err);
            }
        };
        fetchData();
    }, []);

    // Filter Categories
    useEffect(() => {
        setSelectedCategory(null);
        setSelectedProduct(null);
        if (selectedCompany) {
            setFilteredCategories(allData.category.filter((cat) => String(cat.company_id) === String(selectedCompany.company_id)));
        } else {
            setFilteredCategories([]);
        }
    }, [selectedCompany, allData.category]);

    // Filter Products
    useEffect(() => {
        setSelectedProduct(null);
        if (selectedCategory && selectedCompany) {
            const catId = selectedCategory.prcat_cat_id ?? selectedCategory.code ?? selectedCategory.cat_id ?? selectedCategory.id;
            setFilteredProducts(allData.products.filter((p) => String(p.company_id) === String(selectedCompany.company_id) && String(p.prcat_cat_id) === String(catId)));
        } else {
            setFilteredProducts([]);
        }
    }, [selectedCategory, selectedCompany, allData.products]);

    const addToCart = () => {
        if (!selectedProduct) return;
        const exists = cartItems.find((i) => i.id === selectedProduct.spl_id);
        if (exists) {
            setCartItems(cartItems.map((i) => (i.id === selectedProduct.spl_id ? { ...i, quantity: i.quantity + 1 } : i)));
        } else {
            setCartItems([
                ...cartItems,
                {
                    id: selectedProduct.spl_id,
                    name: selectedProduct.product_name,
                    basic_rate: parseFloat(selectedProduct.basic_rate),
                    tax: parseFloat(selectedProduct.basic_tax_percent),
                    quantity: 1.0, // Initialized as float
                    pr_id: selectedProduct.pr_id,
                    category_id: selectedProduct.prcat_cat_id
                }
            ]);
        }
        setSelectedProduct(null);
    };

    const totalSummary = cartItems.reduce(
        (acc, i) => {
            const sub = (i.quantity || 0) * i.basic_rate;
            return { subtotal: acc.subtotal + sub, tax: acc.tax + (sub * i.tax) / 100 };
        },
        { subtotal: 0, tax: 0 }
    );

    const submitFreshOrder = async () => {
        if (!selectedCompany) {
            toast.current.show({ severity: 'warn', summary: 'Missing Info', detail: 'Please select a Customer' });
            return;
        }
        setLoading(true);
        const payload = {
            company_id: selectedCompany.company_id,
            branch_id: sessionInfo.branchid,
            user_id: sessionInfo.userid,
            branchnames: sessionInfo.ubranch,
            order_taken_by: orderTakenBy,
            remarks: remarks,
            order_date: moment(orderDate).format('YYYY-MM-DD'),
            order_time: moment(orderTime).format('hh:mm A'),
            delivery_date: moment(deliveryDate).format('YYYY-MM-DD'),
            delivery_time: deliveryTime ? moment(deliveryTime).format('hh:mm A') : '',
            subtotal: totalSummary.subtotal,
            total_tax_amount: totalSummary.tax,
            grand_total: totalSummary.subtotal + totalSummary.tax,
            items: cartItems.map((item) => ({
                product_id: item.pr_id,
                product_name: item.name,
                spl_id: item.id,
                quantity: item.quantity,
                basic_rate: item.basic_rate,
                tax_percentage: item.tax,
                tax_amount: (item.quantity * item.basic_rate * item.tax) / 100,
                category_id: item.category_id
            }))
        };

        try {
            const response = await insertInstitutionalOrder(payload);

            if (response.status === true) {
                const { bill_no } = response;
                toast.current.show({
                    severity: 'success',
                    summary: 'Order Placed Successfully',
                    detail: `Invoice ${bill_no} has been generated.`,
                    life: 5000
                });
                setTimeout(() => {
                    navigate('/orderlist');
                }, 2000);
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Order Failed',
                    detail: response.message || 'Could not save order',
                    life: 4000
                });
            }
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: err.message || 'Server Error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-3 md:p-4 surface-200 min-h-screen font-sans text-900">
            <Toast ref={toast} />
            <ConfirmDialog />

            {/* TOP BAR */}
            <div className="flex align-items-center justify-content-between mb-3">
                <div className="flex align-items-center gap-3">
                    <Button icon="pi pi-arrow-left" className="p-button-rounded p-button-text p-button-plain surface-0 shadow-1" onClick={() => navigate('/orderlist')} />
                    <h5 className="m-0 font-bold text-2xl tracking-tight text-800">
                        Take <span className="text-primary">Fresh Order</span>
                    </h5>
                </div>
                <div className="font-bold text-sm uppercase px-3 py-2 surface-0 border-round shadow-1 text-700">
                    <i className="pi pi-map-marker mr-2 text-primary"></i>
                    Branch: {sessionInfo.ubranch}
                </div>
            </div>

            {/* ROW 1: SELECTION & DATATABLE */}
            <Card className="mb-4 shadow-3 border-none p-3">
                <div className="p-fluid grid formgrid align-items-end mb-4">
                    <div className="field col-12 md:col-3">
                        <label className="text-sm font-bold text-700 uppercase mb-2 block">1. Select Customer</label>
                        <Dropdown value={selectedCompany} options={allData.company} onChange={(e) => setSelectedCompany(e.value)} optionLabel="company_name" placeholder="Select Customer" filter className="p-inputtext-sm shadow-sm" />
                    </div>
                    <div className="field col-12 md:col-3">
                        <label className="text-sm font-bold text-700 uppercase mb-2 block">2. Category</label>
                        <Dropdown
                            value={selectedCategory}
                            options={filteredCategories}
                            onChange={(e) => setSelectedCategory(e.value)}
                            optionLabel="name"
                            placeholder="Filter Category"
                            disabled={!selectedCompany}
                            className="p-inputtext-sm shadow-sm"
                        />
                    </div>
                    <div className="field col-12 md:col-4">
                        <label className="text-sm font-bold text-700 uppercase mb-2 block">3. Add Product</label>
                        <Dropdown
                            value={selectedProduct}
                            options={filteredProducts}
                            onChange={(e) => setSelectedProduct(e.value)}
                            optionLabel="product_name"
                            placeholder="Search Product..."
                            disabled={!selectedCategory}
                            className="p-inputtext-sm shadow-sm"
                            filter
                        />
                    </div>
                    <div className="field col-12 md:col-2">
                        <Button label="Add to Cart" icon="pi pi-shopping-cart" className="p-button-sm p-button-primary font-bold shadow-2" onClick={addToCart} disabled={!selectedProduct} />
                    </div>
                </div>

                <DataTable value={cartItems} className="p-datatable-sm mt-2" scrollable scrollHeight="400px" size="small" stripedRows emptyMessage="No items added to cart yet.">
                    <Column field="name" header="PRODUCT DESCRIPTION" className="font-bold text-800" style={{ minWidth: '250px' }} />
                    <Column header="RATE" body={(r) => <span className="font-semibold">₹{r.basic_rate.toFixed(2)}</span>} />
                    <Column
                        header="QTY"
                        body={(r) => (
                            <InputNumber
                                value={r.quantity}
                                onValueChange={(e) => setCartItems(cartItems.map((i) => (i.id === r.id ? { ...i, quantity: e.value } : i)))}
                                showButtons
                                min={0}
                                minFractionDigits={0}
                                maxFractionDigits={3}
                                inputClassName="w-6rem p-inputtext-sm text-center font-bold"
                            />
                        )}
                    />
                    <Column header="TAX %" body={(r) => <span className="text-600 font-medium">{r.tax}%</span>} />
                    <Column header="AMOUNT" className="text-right" body={(r) => <span className="font-bold text-blue-700">₹{(r.basic_rate * (r.quantity || 0) * (1 + r.tax / 100)).toFixed(2)}</span>} />
                    <Column style={{ width: '4rem' }} body={(r) => <Button icon="pi pi-trash" className="p-button-danger p-button-text p-button-rounded" onClick={() => setCartItems(cartItems.filter((i) => i.id !== r.id))} />} />
                </DataTable>
            </Card>

            {/* ROW 2: BOTTOM INFO PANELS */}
            <div className="grid grid-nogutter align-items-stretch">
                <div className="col-12 lg:col-8 pr-0 lg:pr-4 pb-4 lg:pb-0">
                    <Card className="shadow-3 border-none h-full flex flex-column">
                        <div className="p-2">
                            <div className="flex align-items-center mb-4 text-primary border-bottom-1 pb-2 border-200">
                                <i className="pi pi-truck mr-2 font-bold text-xl"></i>
                                <h6 className="m-0 text-sm font-bold uppercase tracking-wider">Order & Delivery Details</h6>
                            </div>
                            <div className="p-fluid grid formgrid">
                                <div className="field col-12 md:col-6 px-3">
                                    <label className="text-xs font-bold text-700 uppercase mb-2 block">Order Date</label>
                                    <Calendar value={orderDate} onChange={(e) => setOrderDate(e.value)} dateFormat="dd/mm/yy" showIcon className="p-inputtext-sm" />
                                </div>
                                <div className="field col-12 md:col-6 px-3 mb-4">
                                    <label className="text-xs font-bold text-700 uppercase mb-2 block">Order Time</label>
                                    <Calendar value={orderTime} onChange={(e) => setOrderTime(e.value)} timeOnly hourFormat="12" showIcon className="p-inputtext-sm" />
                                </div>

                                <div className="field col-12 md:col-6 px-3">
                                    <label className="text-xs font-bold text-700 uppercase mb-2 block">Exp. Delivery Date</label>
                                    <Calendar value={deliveryDate} onChange={(e) => setDeliveryDate(e.value)} dateFormat="dd/mm/yy" showIcon className="p-inputtext-sm" />
                                </div>
                                <div className="field col-12 md:col-6 px-3 mb-4">
                                    <label className="text-xs font-bold text-700 uppercase mb-2 block">Exp. Delivery Time</label>
                                    <Calendar value={deliveryTime} onChange={(e) => setDeliveryTime(e.value)} timeOnly hourFormat="12" showIcon className="p-inputtext-sm" />
                                </div>

                                <div className="field col-12 md:col-6 px-3">
                                    <label className="text-xs font-bold text-700 uppercase mb-2 block">Order Taken By</label>
                                    <InputText value={orderTakenBy} onChange={(e) => setOrderTakenBy(e.target.value)} placeholder="User Name" className="p-inputtext-sm" />
                                </div>
                                <div className="field col-12 md:col-6 px-3">
                                    <label className="text-xs font-bold text-700 uppercase mb-2 block">Remarks</label>
                                    <InputText value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Optional" className="p-inputtext-sm" />
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>
                {/* SUMMARY CARD */}
                <div className="col-12 lg:col-4">
                    <Card className="shadow-3 border-none h-full bg-white border-top-3 border-primary flex flex-column justify-content-between">
                        <div className="p-2">
                            <div className="flex align-items-center mb-4 text-700 border-bottom-1 pb-2 border-200">
                                <i className="pi pi-receipt mr-2 font-bold text-xl"></i>
                                <h6 className="m-0 text-sm font-bold uppercase tracking-wider">Billing Summary</h6>
                            </div>

                            <div className="flex justify-content-between mb-3">
                                <span className="text-700 font-semibold">Subtotal:</span>
                                <span className="font-bold text-800">₹{totalSummary.subtotal.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-content-between mb-3">
                                <span className="text-700 font-semibold">Total Tax:</span>
                                <span className="text-orange-600 font-bold">+ ₹{totalSummary.tax.toFixed(2)}</span>
                            </div>

                            <Divider className="my-4" />

                            <div className="flex justify-content-between align-items-center mb-5">
                                <span className="text-lg font-bold text-900">GRAND TOTAL:</span>
                                <span className="text-3xl font-black text-primary">₹{(totalSummary.subtotal + totalSummary.tax).toFixed(2)}</span>
                            </div>
                        </div>

                        <div className="mt-auto">
                            <Button
                                label="PLACE ORDER"
                                icon="pi pi-check-circle"
                                loading={loading}
                                className="p-button-success w-full py-3 font-bold shadow-2 text-lg"
                                onClick={() =>
                                    confirmDialog({
                                        message: 'Confirm and place this fresh order?',
                                        header: 'Order Confirmation',
                                        accept: submitFreshOrder
                                    })
                                }
                                disabled={!cartItems.length || !orderTakenBy || !selectedCompany || loading}
                            />
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default InstitutionSales;
